import { useState } from "react";
import { toast } from "sonner";
import { Sparkle, ThumbsUp, ThumbsDown, FilePdf, FloppyDisk, Calculator, Scales, ShieldCheck } from "@phosphor-icons/react";
import api from "../../../lib/api";
import { formatINR, formatTenure } from "../../../lib/loanMath";
import { AiBeforeAfterChart } from "./AiBeforeAfterChart";
import { AiFollowup } from "./AiFollowup";

const ACT_L = { prepay: "Prepay", close: "Close loan", increase_emi: "Increase EMI", keep_as_is: "Keep as is", build_emergency_fund: "Build emergency fund" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "—");

function Stat({ label, value, testid }) {
  return (
    <div className="card-flat p-3" data-testid={testid}>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="num-metric text-sm mt-0.5">{value}</div>
    </div>
  );
}

function List({ title, items, testid }) {
  if (!items?.length) return null;
  return (
    <section className="card-flat p-4" data-testid={testid}>
      <div className="eyebrow text-[9px] mb-2">{title}</div>
      <ul className="space-y-1.5 text-sm">{items.map((x) => <li key={x} className="flex gap-2"><span className="text-[hsl(var(--primary))]">•</span><span>{x}</span></li>)}</ul>
    </section>
  );
}

function ActionsTable({ actions }) {
  return (
    <div className="card-flat p-4 overflow-x-auto" data-testid="ai-actions">
      <div className="eyebrow text-[9px] mb-2">What to do</div>
      <table className="w-full text-xs !min-w-0">
        <thead><tr className="text-left text-muted-foreground"><th className="py-1 font-normal">Month</th><th className="font-normal">Loan</th><th className="font-normal">Action</th><th className="font-normal">Amount</th><th className="font-normal hidden sm:table-cell">Option</th></tr></thead>
        <tbody>
          {actions.map((a, i) => (
            <tr key={`${a.action}-${a.loan_id}-${a.month}-${i}`} className="border-t hairline" data-testid={`ai-action-row-${i}`}>
              <td className="py-1.5 pr-2">{a.month === 1 ? "Now" : `Month ${a.month}`}</td>
              <td>{a.loan_name}</td>
              <td className="pr-2"><b>{ACT_L[a.action]}</b>{a.option && <span className="sm:hidden block text-[10px] text-muted-foreground">{a.option.replace("_", " ")}</span>}</td>
              <td>{a.amount ? formatINR(a.amount) : "—"}</td>
              <td className="text-muted-foreground hidden sm:table-cell">{a.option ? a.option.replace("_", " ") : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Compare({ s }) {
  const p = s.impact.portfolio;
  const rows = [{ label: s.label, net_saving: p.net_saving, emis_freed_per_month: p.emi_freed, debt_free_date: p.debt_free_after, me: true }, ...(s.bonus_comparison || [])];
  return (
    <div className="card-flat p-4 overflow-x-auto" data-testid="ai-compare-bonus">
      <div className="eyebrow text-[9px] mb-2">Compared with Bonus Advisor strategies (all figures by Estima's calculator)</div>
      <table className="w-full text-xs min-w-[480px]">
        <thead><tr className="text-left text-muted-foreground"><th className="py-1 font-normal">Plan</th><th className="font-normal">Net saving</th><th className="font-normal">EMI freed / month</th><th className="font-normal">Debt-free</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.label} className={`border-t hairline ${r.me ? "font-semibold" : ""}`}>
            <td className="py-1.5">{r.label}</td><td>{formatINR(r.net_saving)}</td><td>{formatINR(r.emis_freed_per_month)}</td><td>{fmtDate(r.debt_free_date)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export function AiResult({ s, onUpdate, onOpenSimulator, onReadDisclaimer }) {
  const [compare, setCompare] = useState(false);
  const [shared, setShared] = useState(false);
  const p = s.impact?.portfolio || {};
  const ai = s.source === "ai";
  const feedback = async (value) => {
    try { await api.post(`/ai-suggestions/${s.id}/feedback`, { value }); onUpdate({ feedback: value }); toast.success("Thanks for the feedback"); } catch { toast.error("Could not save feedback"); }
  };
  const save = async () => {
    const names = s.actions.filter((a) => ["prepay", "close"].includes(a.action)).map((a) => a.loan_name).join(" + ");
    try {
      await api.post("/loans/scenarios/save", {
        name: `AI suggestion ${new Date(s.created_at).toLocaleDateString("en-IN")}`.slice(0, 80),
        loan_name: names ? `AI: ${names}` : "AI suggestion",
        inputs: { source: "ai_suggestion", suggestion_id: s.id, actions: s.actions, answers: s.inputs?.answers },
        summary: { net_saving: p.net_saving, interest_saved: p.interest_saved, emi_freed: p.emi_freed, debt_free_after: p.debt_free_after },
      });
      toast.success("Saved to Saved Scenarios — your loan data is unchanged");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save scenario"); }
  };
  const pdf = async () => {
    try {
      const r = await api.get(`/ai-suggestions/${s.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `Estima_AI_Suggestion_${s.created_at.slice(0, 10)}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not create the PDF"); }
  };
  const firstPrepay = s.actions.find((a) => ["prepay", "close"].includes(a.action));
  const btn = "btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1 border hairline rounded";
  return (
    <div className="space-y-3" data-testid="ai-result">
      <div className="card-flat p-4 sm:p-5 space-y-2">
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${ai ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]" : "bg-muted/30 text-muted-foreground"}`} data-testid="ai-result-label">
          {ai ? <Sparkle size={11} weight="fill" /> : <ShieldCheck size={11} />} {s.label}
        </span>
        {s.note && <div className="text-xs text-[hsl(var(--secondary))]" data-testid="ai-unavailable-note">{s.note}</div>}
        <h3 className="font-serif text-xl sm:text-2xl leading-snug" data-testid="ai-headline">{s.headline}</h3>
        {s.recommended_strategy && <p className="text-sm text-muted-foreground" data-testid="ai-strategy">{s.recommended_strategy}</p>}
      </div>
      <ActionsTable actions={s.actions} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ai-impact">
        <Stat label="Net saving (after charges)" value={formatINR(p.net_saving)} testid="ai-impact-net-saving" />
        <Stat label="Interest saved" value={formatINR(p.interest_saved)} testid="ai-impact-interest-saved" />
        <Stat label="EMI freed / changed" value={`${formatINR(p.emi_freed)}/mo`} testid="ai-impact-emi-freed" />
        <Stat label="Debt-free date" value={`${fmtDate(p.debt_free_after)}${p.months_saved ? ` (${formatTenure(p.months_saved)} sooner)` : ""}`} testid="ai-impact-debt-free" />
      </div>
      <div className="card-flat p-3 grid grid-cols-3 gap-2 text-xs" data-testid="ai-portfolio-strip">
        <div><div className="text-muted-foreground text-[10px]">Outstanding</div>{formatINR(p.outstanding_before, true)} → <b>{formatINR(p.outstanding_after, true)}</b></div>
        <div><div className="text-muted-foreground text-[10px]">Monthly EMI</div>{formatINR(p.emi_before)} → <b>{formatINR(p.emi_after)}</b></div>
        <div><div className="text-muted-foreground text-[10px]">Interest to pay</div>{formatINR(p.interest_before, true)} → <b>{formatINR(p.interest_after, true)}</b></div>
      </div>
      <AiBeforeAfterChart chart={s.impact?.chart} />
      <List title="Why" items={s.reasons} testid="ai-reasons" />
      <List title="Things to keep in mind" items={s.risks} testid="ai-risks" />
      {s.alternatives?.length > 0 && (
        <section className="card-flat p-4" data-testid="ai-alternatives">
          <div className="eyebrow text-[9px] mb-2">Other options</div>
          <ul className="space-y-2 text-sm">{s.alternatives.map((a) => (
            <li key={a.title}><b>{a.title}</b>{a.note && <span className="text-muted-foreground"> — {a.note}</span>}
              {a.impact && <div className="text-[11px] text-muted-foreground">Net saving {formatINR(a.impact.net_saving)} · EMI freed {formatINR(a.impact.emi_freed)}/mo · Debt-free {fmtDate(a.impact.debt_free_after)}</div>}
            </li>))}</ul>
        </section>
      )}
      <List title="Questions for you" items={s.questions_for_user} testid="ai-questions" />
      <div className="flex flex-wrap gap-2" data-testid="ai-result-actions">
        <button type="button" onClick={save} className={btn} data-testid="ai-save-scenario"><FloppyDisk size={12} /> Save as scenario</button>
        <button type="button" onClick={() => onOpenSimulator(firstPrepay)} disabled={!firstPrepay} className={`${btn} disabled:opacity-40`} data-testid="ai-open-simulator"><Calculator size={12} /> Open in Simulator</button>
        <button type="button" onClick={() => setCompare((v) => !v)} className={btn} data-testid="ai-compare-btn"><Scales size={12} /> Compare with Bonus Advisor</button>
        <button type="button" onClick={pdf} className={btn} data-testid="ai-download-pdf"><FilePdf size={12} /> Download PDF</button>
        <span className="ml-auto inline-flex gap-1">
          <button type="button" aria-label="Helpful" onClick={() => feedback("up")} className={`${btn} ${s.feedback === "up" ? "text-[hsl(var(--secondary))]" : ""}`} data-testid="ai-feedback-up"><ThumbsUp size={13} weight={s.feedback === "up" ? "fill" : "regular"} /></button>
          <button type="button" aria-label="Not helpful" onClick={() => feedback("down")} className={`${btn} ${s.feedback === "down" ? "text-destructive" : ""}`} data-testid="ai-feedback-down"><ThumbsDown size={13} weight={s.feedback === "down" ? "fill" : "regular"} /></button>
        </span>
      </div>
      {compare && <Compare s={s} />}
      {ai && <AiFollowup suggestion={s} onUpdate={onUpdate} />}
      <div className="text-[11px] text-muted-foreground border-t hairline pt-2 space-y-1">
        <p data-testid="ai-short-disclaimer">{s.short_disclaimer}</p>
        <div className="flex gap-3 flex-wrap">
          <button type="button" onClick={onReadDisclaimer} className="underline" data-testid="ai-read-full-disclaimer">Read full disclaimer</button>
          {s.shared_with_ai && <button type="button" onClick={() => setShared((v) => !v)} className="underline" data-testid="ai-shared-toggle">{shared ? "Hide" : "See"} exactly what was shared with AI</button>}
        </div>
        {shared && <pre className="text-[10px] max-h-60 overflow-auto border hairline rounded p-2 whitespace-pre-wrap" data-testid="ai-shared-data">{JSON.stringify(s.shared_with_ai, null, 2)}</pre>}
      </div>
    </div>
  );
}
